import * as React from 'react';
import {
  useEffect, useRef, useState,
} from 'react';
import {
  Box, Typography, Stack, Card, Button, CircularProgress, Alert,
  TextField, IconButton, List, ListItem, Divider, Chip, Tabs, Tab,
} from '@mui/material';
import Iconify from '../components/iconify';
import ModalConfirmacao from './ModalConfirmacao';
import {
  getEmpresaAtual, updateEmpresaLogo, updateEmpresaDados,
  getUnidades, addUnidade, updateUnidade, deleteUnidade,
  inativarUnidade, reativarUnidade,
} from './api';
import { cores, PALETA_PADRAO, contraste } from '../styles/styleUtils';
import { useTema } from '../theme';

// Converte um arquivo escolhido pelo usuário em data URI base64
// (mesmo formato já usado nas fotos de módulo/inversor: data:image/png;base64,...)
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Campos de cadastro da aba "Empresa" — combinados com o usuário (seção 4.1
// do HANDOFF v7): CNPJ, razão social, endereço, telefone institucional,
// site, Instagram, e-mail. O "nome" (nome fantasia) já existe e aparece
// como título da tela, fora do formulário.
const CAMPOS_EMPRESA = [
  { chave: 'razaoSocial', label: 'Razão social' },
  { chave: 'cnpj', label: 'CNPJ' },
  { chave: 'endereco', label: 'Endereço' },
  { chave: 'telefone', label: 'Telefone institucional' },
  { chave: 'site', label: 'Site' },
  { chave: 'instagram', label: 'Instagram' },
  { chave: 'email', label: 'E-mail' },
];

// Presets de paleta — ponto de partida rápido para quem não quer digitar
// hex na mão. O usuário ainda pode ajustar qualquer uma das 4 cores depois
// de escolher um preset (ou ignorar os presets e digitar direto).
const PALETAS_PRESET = [
  { nome: 'TriAxis (padrão)', valores: null },
  {
    nome: 'Azul',
    valores: {
      primaria: '#1565C0', escura: '#1A1A2E', neutra: '#5A5A6E', fundoClaro: '#F0F2F8',
    },
  },
  {
    nome: 'Verde',
    valores: {
      primaria: '#2E7D32', escura: '#1B2E1F', neutra: '#5A6E5D', fundoClaro: '#F0F5F1',
    },
  },
  {
    nome: 'Roxo',
    valores: {
      primaria: '#6A3FA0', escura: '#241B2E', neutra: '#6E5A6B', fundoClaro: '#F3F0F5',
    },
  },
];

const CAMPOS_PALETA = [
  { chave: 'primaria', label: 'Cor primária', uso: 'Botões, bordas de destaque, títulos.' },
  { chave: 'escura', label: 'Cor escura', uso: 'Painéis escuros, texto principal.' },
  { chave: 'neutra', label: 'Cor neutra', uso: 'Texto secundário.' },
  { chave: 'fundoClaro', label: 'Cor de fundo claro', uso: 'Fundos de tela e cartões.' },
];

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const LogoCard = ({
  titulo, descricao, campo, valor, fundoEscuro, onUpload, uploading,
}) => {
  const inputRef = useRef(null);

  return (
    <Card variant="outlined" sx={{ p: 2, flex: 1, minWidth: 240 }}>
      <Typography variant="subtitle1" fontWeight="bold" mb={0.5}>{titulo}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {descricao}
      </Typography>

      <Box sx={{
        height: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #ccc',
        borderRadius: 1,
        mb: 1.5,
        p: 1,
        background: fundoEscuro
          ? `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`
          : '#fafafa',
      }}
      >
        {valor ? (
          <img src={valor} alt={titulo} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
        ) : (
          <Typography variant="caption" sx={{ color: fundoEscuro ? 'rgba(255,255,255,0.5)' : 'text.secondary' }}>
            Nenhuma logo cadastrada
          </Typography>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(campo, file);
          e.target.value = ''; // permite escolher o mesmo arquivo de novo, se precisar
        }}
      />
      <Button
        variant="outlined"
        size="small"
        fullWidth
        disabled={uploading === campo}
        startIcon={uploading === campo ? <CircularProgress size={14} /> : <Iconify icon="eva:upload-outline" />}
        onClick={() => inputRef.current?.click()}
      >
        {uploading === campo ? 'Enviando...' : 'Substituir'}
      </Button>
    </Card>
  );
};

// Cor com preview (input nativo type="color") + campo de texto com o hex,
// sincronizados — "o usuário escolhe na paleta ou digita o código" (seção
// 4.2 do HANDOFF v7).
const CampoCor = ({
  label, uso, value, onChange,
}) => (
  <Stack direction="row" alignItems="center" gap={1.5} sx={{ flex: 1, minWidth: 240 }}>
    <Box
      component="input"
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        width: 40, height: 40, minWidth: 40, border: '1px solid #ccc', borderRadius: 1, p: 0, cursor: 'pointer',
      }}
    />
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" fontWeight="bold">{label}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>{uso}</Typography>
      <TextField size="small" value={value} onChange={(e) => onChange(e.target.value)} fullWidth />
    </Box>
  </Stack>
);

export default function ConfiguracaoEmpresa() {
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [msg, setMsg] = useState(null);
  const [aba, setAba] = useState(0);

  // Aba "Empresa" — cadastro
  const [dadosEmpresa, setDadosEmpresa] = useState({});
  const [salvandoDados, setSalvandoDados] = useState(false);

  // Aba "Unidades"
  const [unidades, setUnidades] = useState([]);
  const [novaUnidade, setNovaUnidade] = useState('');
  const [novoEnderecoUnidade, setNovoEnderecoUnidade] = useState('');
  const [salvandoUnidade, setSalvandoUnidade] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editandoNome, setEditandoNome] = useState('');
  const [editandoEndereco, setEditandoEndereco] = useState('');
  const [unidadeParaExcluir, setUnidadeParaExcluir] = useState(null);
  // Quando a exclusão física é bloqueada (unidade com vínculo), guarda a
  // unidade aqui para oferecer a opção de inativar em vez de excluir.
  const [unidadeParaInativar, setUnidadeParaInativar] = useState(null);

  // Aba "Identidade Visual" — paleta
  const { paleta, trocarPaleta } = useTema();
  const [rascunhoPaleta, setRascunhoPaleta] = useState(paleta || PALETA_PADRAO);
  const [presetSelecionado, setPresetSelecionado] = useState(null);
  const [salvandoPaleta, setSalvandoPaleta] = useState(false);
  // Guarda a última paleta efetivamente salva, para restaurar se o usuário
  // trocar de aba sem salvar a pré-visualização (ver useEffect de cleanup
  // dentro do painel da aba, mais abaixo).
  const paletaPersistidaRef = useRef(paleta || null);

  const fetchEmpresa = async () => {
    setLoading(true);
    const data = await getEmpresaAtual();
    setEmpresa(data);
    if (data) {
      setDadosEmpresa({
        razaoSocial: data.razaoSocial || '',
        cnpj: data.cnpj || '',
        endereco: data.endereco || '',
        telefone: data.telefone || '',
        site: data.site || '',
        instagram: data.instagram || '',
        email: data.email || '',
      });
      paletaPersistidaRef.current = data.paleta || null;
    }
    if (data?._id) {
      // incluirInativas: true — a tela de administração precisa ver tudo,
      // inclusive unidades já inativadas, para poder reativá-las depois.
      const listaUnidades = await getUnidades(data._id, { incluirInativas: true });
      setUnidades(listaUnidades || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEmpresa();
  }, []);

  const handleUpload = async (campo, file) => {
    setMsg(null);
    setUploading(campo);
    try {
      const base64 = await fileToBase64(file);
      const result = await updateEmpresaLogo(empresa._id, { [campo]: base64 });
      if (result?.data) {
        setEmpresa(result.data);
        setMsg({ tipo: 'success', texto: 'Logo atualizada com sucesso.' });
      } else {
        setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao atualizar a logo.' });
      }
    } catch (e) {
      setMsg({ tipo: 'error', texto: 'Erro ao enviar a imagem. Tente novamente.' });
    }
    setUploading(null);
  };

  // ---- Aba Empresa (cadastro) ----

  const handleSalvarDadosEmpresa = async () => {
    setMsg(null);
    setSalvandoDados(true);
    const result = await updateEmpresaDados(empresa._id, dadosEmpresa);
    if (result?.data) {
      setEmpresa(result.data);
      setMsg({ tipo: 'success', texto: 'Dados da empresa salvos com sucesso.' });
    } else {
      setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao salvar os dados da empresa.' });
    }
    setSalvandoDados(false);
  };

  // ---- Aba Unidades ----

  const handleAddUnidade = async () => {
    const nome = novaUnidade.trim();
    if (!nome) return;
    setSalvandoUnidade(true);
    const result = await addUnidade(nome, empresa._id, novoEnderecoUnidade.trim() || undefined);
    if (result?.data) {
      setUnidades((prev) => [...prev, result.data].sort((a, b) => a.nome.localeCompare(b.nome)));
      setNovaUnidade('');
      setNovoEnderecoUnidade('');
    } else {
      setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao adicionar unidade.' });
    }
    setSalvandoUnidade(false);
  };

  const handleIniciarEdicao = (unidade) => {
    setEditandoId(unidade._id);
    setEditandoNome(unidade.nome);
    setEditandoEndereco(unidade.endereco || '');
  };

  const handleCancelarEdicao = () => {
    setEditandoId(null);
    setEditandoNome('');
    setEditandoEndereco('');
  };

  const handleSalvarEdicao = async (id) => {
    const nome = editandoNome.trim();
    if (!nome) return;
    setSalvandoUnidade(true);
    const result = await updateUnidade(id, nome, editandoEndereco.trim() || undefined);
    if (result?.data) {
      setUnidades((prev) => prev
        .map((u) => (u._id === id ? result.data : u))
        .sort((a, b) => a.nome.localeCompare(b.nome)));
      handleCancelarEdicao();
    } else {
      setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao atualizar unidade.' });
    }
    setSalvandoUnidade(false);
  };

  const handleExcluirUnidade = async () => {
    const unidade = unidadeParaExcluir;
    const id = unidade._id;
    setUnidadeParaExcluir(null);
    const result = await deleteUnidade(id);
    if (result?.ok !== false && !result?.msg) {
      setUnidades((prev) => prev.filter((u) => u._id !== id));
      setMsg(null);
    } else {
      // Backend bloqueou por vínculo (usuário/proposta) — oferece inativar
      // como alternativa, em vez de só mostrar o erro e travar por aí.
      setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao excluir unidade.' });
      setUnidadeParaInativar(unidade);
    }
  };

  const handleInativarUnidade = async () => {
    const unidade = unidadeParaInativar;
    setUnidadeParaInativar(null);
    const result = await inativarUnidade(unidade._id);
    if (result?.data) {
      setUnidades((prev) => prev.map((u) => (u._id === unidade._id ? result.data : u)));
      setMsg({ tipo: 'success', texto: `Unidade "${unidade.nome}" inativada. O histórico continua intacto.` });
    } else {
      setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao inativar unidade.' });
    }
  };

  const handleReativarUnidade = async (unidade) => {
    const result = await reativarUnidade(unidade._id);
    if (result?.data) {
      setUnidades((prev) => prev.map((u) => (u._id === unidade._id ? result.data : u)));
      setMsg({ tipo: 'success', texto: `Unidade "${unidade.nome}" reativada.` });
    } else {
      setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao reativar unidade.' });
    }
  };

  // ---- Aba Identidade Visual (paleta) ----

  // Pré-visualização ao vivo: toda mudança no rascunho já reflete na tela
  // inteira via trocarPaleta(), como recomendado na seção 4.2 do handoff.
  useEffect(() => {
    trocarPaleta(rascunhoPaleta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rascunhoPaleta]);

  // Se o usuário sair desta aba sem salvar, desfaz a pré-visualização e
  // volta pra última paleta realmente persistida — senão o app inteiro
  // fica "preso" numa cor que não foi salva até a página recarregar.
  useEffect(() => () => {
    trocarPaleta(paletaPersistidaRef.current || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAplicarPreset = (presetObj) => {
    setRascunhoPaleta(presetObj.valores || PALETA_PADRAO);
    setPresetSelecionado(presetObj.nome);
  };

  const handleMudarCor = (chave, valor) => {
    setRascunhoPaleta((prev) => ({ ...prev, [chave]: valor }));
    setPresetSelecionado(null);
  };

  const handleSalvarPaleta = async () => {
    setMsg(null);
    setSalvandoPaleta(true);
    const result = await updateEmpresaDados(empresa._id, { paleta: rascunhoPaleta });
    if (result?.data) {
      setEmpresa(result.data);
      paletaPersistidaRef.current = rascunhoPaleta;
      setMsg({ tipo: 'success', texto: 'Paleta salva com sucesso.' });
    } else {
      setMsg({ tipo: 'error', texto: result?.msg || 'Erro ao salvar a paleta.' });
    }
    setSalvandoPaleta(false);
  };

  // Achado de acessibilidade documentado no handoff (seção 3.3): o
  // contraste de texto branco sobre cores de marca pode ficar abaixo do
  // mínimo WCAG AA (4.5). Aqui só avisamos, não bloqueamos — a decisão de
  // manter uma cor de manual de marca fora do ideal é do usuário.
  const avisosContraste = [];
  if (contraste('#FFFFFF', rascunhoPaleta.primaria) < 4.5) {
    avisosContraste.push('Texto branco sobre a cor primária escolhida fica com contraste abaixo do recomendado — pode ficar difícil de ler em botões.');
  }
  if (contraste('#FFFFFF', rascunhoPaleta.escura) < 4.5) {
    avisosContraste.push('Texto branco sobre a cor escura escolhida fica com contraste abaixo do recomendado.');
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} p={2}>
        <CircularProgress size={18} />
        <Typography variant="body2">Carregando dados da empresa...</Typography>
      </Box>
    );
  }

  if (!empresa) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Nenhuma empresa cadastrada ainda. Rode o script <code>seedEmpresaUnidades.js</code> no backend.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight="bold" mb={2}>{empresa.nome}</Typography>

      {msg && (
        <Alert severity={msg.tipo} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.texto}
        </Alert>
      )}

      <Tabs value={aba} onChange={(_, novaAba) => setAba(novaAba)}>
        <Tab label="Empresa" />
        <Tab label="Unidades" />
        <Tab label="Identidade Visual" />
      </Tabs>
      <Divider />

      {/* ---------- Aba 1 — Empresa ---------- */}
      <TabPanel value={aba} index={0}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Dados institucionais da empresa. Alguns destes campos serão usados para
          personalizar automaticamente as propostas comerciais em PDF (seção 4.3 do
          planejamento — CNPJ, razão social e endereço).
        </Typography>

        <Card variant="outlined" sx={{ p: 2, maxWidth: 560 }}>
          <Stack gap={2}>
            {CAMPOS_EMPRESA.map((campo) => (
              <TextField
                key={campo.chave}
                size="small"
                label={campo.label}
                value={dadosEmpresa[campo.chave] || ''}
                onChange={(e) => setDadosEmpresa((prev) => ({ ...prev, [campo.chave]: e.target.value }))}
                fullWidth
              />
            ))}
            <Button
              variant="contained"
              disabled={salvandoDados}
              startIcon={salvandoDados ? <CircularProgress size={14} color="inherit" /> : <Iconify icon="eva:save-outline" />}
              onClick={handleSalvarDadosEmpresa}
              sx={{ alignSelf: 'flex-start' }}
            >
              Salvar dados
            </Button>
          </Stack>
        </Card>
      </TabPanel>

      {/* ---------- Aba 2 — Unidades ---------- */}
      <TabPanel value={aba} index={1}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Unidades desta empresa (ex.: filiais, escritórios). Usadas para filtrar dados e,
          futuramente, para segmentar acesso por unidade. Unidades com histórico vinculado
          (usuários ou propostas) não podem ser excluídas — apenas inativadas.
        </Typography>

        <Card variant="outlined" sx={{ p: 2, maxWidth: 560 }}>
          <List dense disablePadding>
            {unidades.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Nenhuma unidade cadastrada ainda.
              </Typography>
            )}
            {unidades.map((unidade) => {
              const inativa = unidade.ativo === false;
              return (
                <ListItem
                  key={unidade._id}
                  disableGutters
                  sx={{ opacity: inativa ? 0.6 : 1, alignItems: 'flex-start' }}
                  secondaryAction={editandoId === unidade._id ? (
                    <Stack direction="row" gap={0.5}>
                      <IconButton size="small" color="success" onClick={() => handleSalvarEdicao(unidade._id)}>
                        <Iconify icon="eva:checkmark-fill" width={18} height={18} />
                      </IconButton>
                      <IconButton size="small" onClick={handleCancelarEdicao}>
                        <Iconify icon="eva:close-fill" width={18} height={18} />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" gap={0.5}>
                      {inativa ? (
                        <IconButton
                          size="small"
                          color="success"
                          title="Reativar unidade"
                          onClick={() => handleReativarUnidade(unidade)}
                        >
                          <Iconify icon="eva:refresh-outline" width={18} height={18} />
                        </IconButton>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => handleIniciarEdicao(unidade)}>
                            <Iconify icon="eva:edit-outline" width={18} height={18} />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title="Excluir ou inativar"
                            onClick={() => setUnidadeParaExcluir(unidade)}
                          >
                            <Iconify icon="eva:trash-2-outline" width={18} height={18} />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  )}
                >
                  {editandoId === unidade._id ? (
                    <Stack gap={1} sx={{ width: '100%', mr: 8 }}>
                      <TextField
                        size="small"
                        label="Nome"
                        value={editandoNome}
                        onChange={(e) => setEditandoNome(e.target.value)}
                        fullWidth
                        autoFocus
                      />
                      <TextField
                        size="small"
                        label="Endereço"
                        value={editandoEndereco}
                        onChange={(e) => setEditandoEndereco(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSalvarEdicao(unidade._id)}
                        fullWidth
                      />
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.5 }}>
                      <Box>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="body2">{unidade.nome}</Typography>
                          {inativa && <Chip label="Inativa" size="small" variant="outlined" />}
                        </Stack>
                        {unidade.endereco && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {unidade.endereco}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  )}
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 1.5 }} />

          <Stack gap={1}>
            <TextField
              size="small"
              placeholder="Nome da nova unidade"
              value={novaUnidade}
              onChange={(e) => setNovaUnidade(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              placeholder="Endereço (opcional)"
              value={novoEnderecoUnidade}
              onChange={(e) => setNovoEnderecoUnidade(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddUnidade()}
              fullWidth
            />
            <Button
              variant="contained"
              size="small"
              disabled={salvandoUnidade || !novaUnidade.trim()}
              startIcon={salvandoUnidade ? <CircularProgress size={14} color="inherit" /> : <Iconify icon="zondicons:add-solid" />}
              onClick={handleAddUnidade}
              sx={{ alignSelf: 'flex-start' }}
            >
              Adicionar
            </Button>
          </Stack>
        </Card>

        {unidadeParaExcluir && (
          <ModalConfirmacao
            title="Tem certeza que deseja excluir essa unidade?"
            subtitle={`${unidadeParaExcluir.nome} — se houver histórico vinculado, a exclusão será bloqueada e você poderá inativar no lugar.`}
            open={!!unidadeParaExcluir}
            onClose={() => setUnidadeParaExcluir(null)}
            onConfirm={handleExcluirUnidade}
          />
        )}

        {unidadeParaInativar && (
          <ModalConfirmacao
            title="Esta unidade não pode ser excluída"
            subtitle={`"${unidadeParaInativar.nome}" tem histórico vinculado (usuários ou propostas). Deseja inativá-la em vez de excluir? O histórico continua intacto e a unidade some das opções de novos cadastros.`}
            open={!!unidadeParaInativar}
            onClose={() => setUnidadeParaInativar(null)}
            onConfirm={handleInativarUnidade}
          />
        )}
      </TabPanel>

      {/* ---------- Aba 3 — Identidade Visual ---------- */}
      <TabPanel value={aba} index={2}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Estas imagens são usadas nas capas e nos cabeçalhos dos PDFs de propostas e relatórios
          gerados pelo sistema. Envie arquivos PNG.
        </Typography>

        <Stack direction="row" gap={2} flexWrap="wrap">
          <LogoCard
            titulo="Logo Principal"
            descricao="Usada em fundo claro (capa das propostas)."
            campo="logoPrincipal"
            valor={empresa.logoPrincipal}
            fundoEscuro={false}
            onUpload={handleUpload}
            uploading={uploading}
          />
          <LogoCard
            titulo="Logo Branca"
            descricao="Usada em fundo escuro (cabeçalho dos relatórios)."
            campo="logoBranca"
            valor={empresa.logoBranca}
            fundoEscuro
            onUpload={handleUpload}
            uploading={uploading}
          />
          <LogoCard
            titulo="Logo de Parceiro"
            descricao="Selo opcional (ex.: parceria com fabricante). Pode ficar vazio."
            campo="logoParceiro"
            valor={empresa.logoParceiro}
            fundoEscuro={false}
            onUpload={handleUpload}
            uploading={uploading}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight="bold" mb={0.5}>Paleta de cores</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Escolha um ponto de partida abaixo ou digite o código de cada cor. A pré-visualização
          é aplicada na hora em toda a tela — nada é salvo até clicar em &quot;Salvar paleta&quot;.
        </Typography>

        <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
          {PALETAS_PRESET.map((p) => (
            <Chip
              key={p.nome}
              label={p.nome}
              variant={presetSelecionado === p.nome ? 'filled' : 'outlined'}
              color={presetSelecionado === p.nome ? 'primary' : 'default'}
              onClick={() => handleAplicarPreset(p)}
            />
          ))}
        </Stack>

        <Card variant="outlined" sx={{ p: 2, maxWidth: 640 }}>
          <Stack gap={2}>
            {CAMPOS_PALETA.map((campo) => (
              <CampoCor
                key={campo.chave}
                label={campo.label}
                uso={campo.uso}
                value={rascunhoPaleta[campo.chave]}
                onChange={(valor) => handleMudarCor(campo.chave, valor)}
              />
            ))}
          </Stack>

          {avisosContraste.map((aviso) => (
            <Alert severity="warning" key={aviso} sx={{ mt: 2 }}>{aviso}</Alert>
          ))}

          <Stack direction="row" gap={1} mt={2}>
            <Button
              variant="contained"
              disabled={salvandoPaleta}
              startIcon={salvandoPaleta ? <CircularProgress size={14} color="inherit" /> : <Iconify icon="eva:save-outline" />}
              onClick={handleSalvarPaleta}
            >
              Salvar paleta
            </Button>
            <Button variant="text" onClick={() => handleAplicarPreset(PALETAS_PRESET[0])}>
              Restaurar padrão TriAxis
            </Button>
          </Stack>
        </Card>
      </TabPanel>
    </Box>
  );
}